import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Value } from "firebase/remote-config";
import { REMOTE_CONFIG_QUERY_KEY } from "./queryKey";
import { reportUnavailable } from "./reporting";
import { getSnapshotFailure, remoteConfigQueryFn } from "./subscription";
import type { RemoteConfigResult } from "./types";

type Options = {
  // Ignore live updates; the resolved value holds until the next page load
  sampleOnce?: boolean;
  // Warn Sentry when this consumer falls back to its hardcoded default.
  // Opt-in per call — currently AU residents in the signup phone verification
  // flow (PKB-13158)
  reportFailures?: boolean;
};

const sampledValues = new Map<string, Value | null>();
const resetSampledValues = () => sampledValues.clear();

const useRemoteConfig = (
  key: string,
  { sampleOnce = false, reportFailures = false }: Options = {},
): RemoteConfigResult => {
  const { data, isPending } = useQuery({
    queryKey: REMOTE_CONFIG_QUERY_KEY,
    queryFn: remoteConfigQueryFn,
    select: (snapshot) => snapshot[key] ?? null,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY, // listener is installed once and never torn down
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // A failed fetch resolves with whatever is cached and arms the delayed
    // invalidate in scheduleRefetch, so recovery needs no retry here — and
    // never holds `loading` true the way a backoff loop would.
    retry: false,
  });

  if (sampleOnce && !isPending && !sampledValues.has(key)) {
    sampledValues.set(key, data ?? null);
  }

  const value = sampleOnce ? sampledValues.get(key) ?? null : data ?? null;

  // A null value here means the consumer is about to use its own fallback,
  // whether the config was unavailable or the key is missing.
  useEffect(() => {
    if (!reportFailures || isPending || value !== null) return;
    const failure = getSnapshotFailure();
    reportUnavailable(key, failure?.reason ?? "missing_key", failure?.error);
  }, [reportFailures, isPending, value, key]);

  return { loading: isPending, value };
};

export { useRemoteConfig, resetSampledValues };
